import React, { useMemo, useRef } from 'react';

import { CaseStudyListItem, caseStudySubtitle } from 'stile-shared/src/utils/case-studies';
import styled from 'styled-components';

import SwipeIndicator from '../SwipeIndicator/SwipeIndicator';
import CaseStudyTile from './CaseStudyTile';

const Container = styled.div`
  width: 100%;
  max-width: 1160px;
  margin: 0 auto;
  padding: 80px 0 20px;
  background-color: var(--stile-grey-1);

  @media (max-width: 1220px) {
    padding: 60px 30px;
  }

  @media (max-width: 768px) {
    padding: 38px 0 60px;
  }
`;

const Title = styled.h4`
  margin-bottom: 32px;
  @media (max-width: 768px) {
    margin: 22px 0 28px 25px;
  }
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-gap: 28px;
  width: 100%;

  @media (max-width: 768px) {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0 20px;
    gap: 15px;
    & > div {
      scroll-snap-align: start;
      flex: 0 0 90%;
      max-width: 90%;
    }

    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
  }
`;

const MSICon = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    margin: 16px 20px 0;
    position: relative;
  }
`;

interface MoreCaseStudiesProps {
  currentCaseStudyId: string;
  caseStudies: CaseStudyListItem[];
  maxDisplayCount?: number;
  title?: string;
  basePath?: string;
}

const MoreCaseStudies: React.FC<MoreCaseStudiesProps> = ({
  currentCaseStudyId,
  caseStudies,
  maxDisplayCount = 3,
  title = 'More case studies',
  basePath = '/why-choose-stile/case-studies',
}) => {
  const displayStudies = useMemo(() => {
    const total = caseStudies.length;
    if (total <= 1) return [];

    const currentIdx = caseStudies.findIndex((cs) => cs.id === currentCaseStudyId);
    if (currentIdx === -1) return []; // safety – ID not found

    const result: CaseStudyListItem[] = [];
    // Collect up to maxDisplayCount next items
    for (let step = 1; step <= Math.min(maxDisplayCount, total - 1); step++) {
      const idx = (currentIdx + step) % total;
      result.push(caseStudies[idx]);
    }
    return result;
  }, [caseStudies, currentCaseStudyId, maxDisplayCount]);

  const swiperRef = useRef<HTMLDivElement>(null);

  if (!displayStudies.length) return null;

  return (
    <Container>
      <Title>{title}</Title>
      <CaseStudyGrid ref={swiperRef}>
        {displayStudies.map((study) => (
          <CaseStudyTile
            key={study.id}
            imgSrc={study.imageData}
            title={study.title}
            school={caseStudySubtitle(study)}
            description={study.description}
            url={`${basePath}/${study.slug}`}
            slug={study.slug}
          />
        ))}
      </CaseStudyGrid>
      <MSICon>
        <SwipeIndicator containerRef={swiperRef} />
      </MSICon>
    </Container>
  );
};

export default MoreCaseStudies;
